import { CC, type MidiEvent } from "./parse";

/** Anything that can play notes. Velocity is normalised 0–1. */
export interface NoteTarget {
  noteOn(note: number, velocity: number): void;
  noteOff(note: number): void;
  /** Optional continuous controls. */
  pitchBend?(semitones: number): void;
  modulation?(amount: number): void;
}

export interface RouterOptions {
  /** "omni" or a channel 1–16. */
  channel: "omni" | number;
  /** Semitones applied to live input (0 = off). Out-of-range results are dropped. */
  transpose: number;
  /** Pitch-bend range in semitones (GM default ±2). */
  bendRange?: number;
}

/**
 * Routes parsed MIDI to a NoteTarget: channel filter, live transposition and
 * sustain-pedal (CC64 ≥ 64 = down) handling. Tracks the transposed note it
 * played for each incoming note so that note-off releases the correct pitch
 * even if transposition changed while the key was held.
 */
export class MidiInputRouter {
  private held = new Map<string, number>(); // `${ch}:${note}` → sounding note
  private sustained = new Set<number>(); // sounding notes released while pedal down
  private pedalDown = false;
  private bent = false;
  private modulated = false;

  constructor(private target: NoteTarget, private options: RouterOptions = { channel: "omni", transpose: 0 }) {}

  setOptions(options: Partial<RouterOptions>): void {
    this.options = { ...this.options, ...options };
  }

  get sustainPedal(): boolean {
    return this.pedalDown;
  }

  handle(e: MidiEvent): void {
    if (!("channel" in e)) return;
    if (this.options.channel !== "omni" && e.channel !== this.options.channel) return;
    switch (e.type) {
      case "noteon": {
        const sounding = e.note + this.options.transpose;
        if (sounding < 0 || sounding > 127) return;
        const k = `${e.channel}:${e.note}`;
        const prev = this.held.get(k);
        if (prev !== undefined) this.target.noteOff(prev);
        this.sustained.delete(sounding);
        this.held.set(k, sounding);
        this.target.noteOn(sounding, e.velocity / 127);
        return;
      }
      case "noteoff": {
        const k = `${e.channel}:${e.note}`;
        const sounding = this.held.get(k);
        if (sounding === undefined) return;
        this.held.delete(k);
        if (this.pedalDown) this.sustained.add(sounding);
        else this.target.noteOff(sounding);
        return;
      }
      case "pitchbend": {
        // -8192..8191 → -range..+range (asymmetric 14-bit range mapped so both extremes are exact).
        const norm = e.value >= 0 ? e.value / 8191 : e.value / 8192;
        this.bent = e.value !== 0;
        this.target.pitchBend?.(norm * (this.options.bendRange ?? 2));
        return;
      }
      case "cc":
        if (e.controller === CC.MOD_WHEEL) { this.modulated = e.value > 0; this.target.modulation?.(e.value / 127); }
        else if (e.controller === CC.SUSTAIN) this.setPedal(e.value >= 64);
        else if (e.controller === CC.ALL_NOTES_OFF || e.controller === CC.ALL_SOUND_OFF) this.releaseAll();
        return;
      default:
        return;
    }
  }

  private setPedal(down: boolean): void {
    if (down === this.pedalDown) return;
    this.pedalDown = down;
    if (!down) {
      const stillHeld = new Set(this.held.values());
      for (const n of this.sustained) if (!stillHeld.has(n)) this.target.noteOff(n);
      this.sustained.clear();
    }
  }

  /** Release everything this router started (used by panic and device disconnect). */
  releaseAll(): void {
    for (const n of new Set([...this.held.values(), ...this.sustained])) this.target.noteOff(n);
    this.held.clear();
    this.sustained.clear();
    this.pedalDown = false;
    if (this.bent) { this.target.pitchBend?.(0); this.bent = false; }
    if (this.modulated) { this.target.modulation?.(0); this.modulated = false; }
  }
}
